// src/QRmessaging/DirectQRCreator.js

import React, { useState, useRef, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import './DirectQRCreator.css';

const DirectQRCreator = ({ language }) => {
  const [step, setStep] = useState(1); // 1: 템플릿 선택, 2: 내용 입력, 3: QR 코드 생성, 4: 이미지 업로드 및 배치
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [qrValue, setQrValue] = useState('');
  const [qrGenerated, setQrGenerated] = useState(false);
  const [qrTitle, setQrTitle] = useState('');
  const [userImage, setUserImage] = useState(null);
  const [qrPosition, setQrPosition] = useState({ x: 50, y: 50 }); // 기본 위치 (%)
  const [isDragging, setIsDragging] = useState(false);
  const [qrSize, setQrSize] = useState(25); // QR 코드 크기 (% 단위)
  const [qrOpacity, setQrOpacity] = useState(0.7); // QR 코드 불투명도
  const imageRef = useRef(null);
  const canvasRef = useRef(null);
  
  // 사전 정의된 템플릿 목록
  const templates = [
    { id: 'text', name: language === 'KR' ? '텍스트' : 'Text', icon: 'fas fa-font' },
    { id: 'url', name: language === 'KR' ? 'URL' : 'URL', icon: 'fas fa-link' },
    { id: 'email', name: language === 'KR' ? '이메일' : 'Email', icon: 'fas fa-envelope' },
    { id: 'phone', name: language === 'KR' ? '전화번호' : 'Phone Number', icon: 'fas fa-phone' },
    { id: 'vcard', name: language === 'KR' ? '연락처' : 'Contact', icon: 'fas fa-address-card' },
    { id: 'sms', name: language === 'KR' ? 'SMS' : 'SMS', icon: 'fas fa-comment-dots' },
  ];
  
  // 템플릿 선택 처리
  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setStep(2);
  };
  
  // 입력값 변경 처리
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };
  
  // QR 코드 생성 처리
  const handleGenerateQR = () => {
    if (!inputValue.trim()) {
      alert(language === 'KR' ? '내용을 입력해주세요.' : 'Please enter content.');
      return;
    }
    
    let formattedValue = inputValue;
    
    // 템플릿에 따라 QR 코드 내용 형식 지정
    switch (selectedTemplate.id) {
      case 'url':
        if (!inputValue.startsWith('http://') && !inputValue.startsWith('https://')) {
          formattedValue = 'https://' + inputValue;
        }
        break;
      case 'email':
        formattedValue = 'mailto:' + inputValue;
        break;
      case 'phone':
        formattedValue = 'tel:' + inputValue;
        break;
      case 'sms':
        formattedValue = 'sms:' + inputValue;
        break;
      case 'vcard':
        const vcardInfo = JSON.parse(inputValue);
        formattedValue = `BEGIN:VCARD
VERSION:3.0
FN:${vcardInfo.name || ''}
${vcardInfo.title ? `TITLE:${vcardInfo.title}` : ''}
${vcardInfo.company ? `ORG:${vcardInfo.company}` : ''}
${vcardInfo.email ? `EMAIL:${vcardInfo.email}` : ''}
${vcardInfo.phone ? `TEL:${vcardInfo.phone}` : ''}
${vcardInfo.address ? `ADR:;;${vcardInfo.address};;;` : ''}
END:VCARD`;
        break;
      default:
        formattedValue = inputValue;
    }
    
    setQrValue(formattedValue);
    setQrTitle(selectedTemplate.name);
    setQrGenerated(true);
    
    // 일반 QR 코드 생성 후 이미지 업로드 단계로 진행
    alert(language === 'KR' 
      ? 'QR 코드가 생성되었습니다. 이미지에 QR 코드를 삽입하려면 다음 단계로 진행하세요.' 
      : 'QR code generated. Proceed to the next step to embed it on an image.');
    setStep(3);
  };
  
  // 이미지 업로드 처리
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUserImage(event.target.result);
        // 이미지가 업로드되면 다음 단계로 진행
        setStep(4);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // QR 코드 드래그 시작
  const handleMouseDown = (e) => {
    if (!imageRef.current) return;
    
    setIsDragging(true);
    
    // 상대 좌표 계산
    updateQrPosition(e);
  };
  
  // QR 코드 드래그 중
  const handleMouseMove = (e) => {
    if (!isDragging || !imageRef.current) return;
    
    // 상대 좌표 계산
    updateQrPosition(e);
  };
  
  // QR 코드 드래그 종료
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  
  // QR 코드 위치 업데이트
  const updateQrPosition = (e) => {
    const imageRect = imageRef.current.getBoundingClientRect();
    
    // 이미지 내에서의 상대 좌표 계산 (%)
    const x = Math.min(Math.max(((e.clientX - imageRect.left) / imageRect.width) * 100, 0), 100);
    const y = Math.min(Math.max(((e.clientY - imageRect.top) / imageRect.height) * 100, 0), 100);
    
    setQrPosition({ x, y });
  };
  
  // QR 코드 저장 처리 (일반 QR 코드 또는 이미지+QR 코드)
  const handleSaveQR = () => {
    if (step === 4 && userImage) {
      saveQrWithImage();
    } else {
      saveQrOnly();
    }
  };
  
  // 이미지와 QR 코드를 함께 저장
  const saveQrWithImage = () => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // 원본 이미지 로드
      const img = new Image();
      img.onload = function() {
        // 캔버스 크기 설정
        canvas.width = img.width;
        canvas.height = img.height;
        
        // 먼저 이미지 그리기
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // QR 코드 SVG 불러오기
        const svgElement = document.querySelector('#overlayQrCodeContainer svg');
        const svgData = new XMLSerializer().serializeToString(svgElement);
        const svgBase64 = btoa(unescape(encodeURIComponent(svgData)));
        
        const qrImg = new Image();
        qrImg.onload = function() {
          // QR 코드 크기 계산 (원본 이미지 크기의 qrSize%)
          const qrWidth = canvas.width * (qrSize / 100);
          const qrHeight = qrWidth; // 정사각형 유지
          
          // QR 코드 위치 계산
          const qrX = (canvas.width * qrPosition.x / 100) - (qrWidth / 2);
          const qrY = (canvas.height * qrPosition.y / 100) - (qrHeight / 2);
          
          // 반투명 회색 QR 코드 그리기
          ctx.globalAlpha = qrOpacity;
          ctx.drawImage(qrImg, qrX, qrY, qrWidth, qrHeight);
          ctx.globalAlpha = 1.0;
          
          // 결과 이미지 다운로드
          const finalImageUrl = canvas.toDataURL('image/png');
          const downloadLink = document.createElement('a');
          downloadLink.href = finalImageUrl;
          downloadLink.download = `qr-embedded-image-${Date.now()}.png`;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
        };
        qrImg.src = 'data:image/svg+xml;base64,' + svgBase64;
      };
      img.src = userImage;
    } catch (error) {
      console.error('이미지 저장 오류:', error);
      alert(language === 'KR' 
        ? '이미지 저장 중 오류가 발생했습니다.' 
        : 'Error saving image.');
    }
  };
  
  // QR 코드만 저장 (이미지 없음)
  const saveQrOnly = () => {
    try {
      // SVG 요소 선택 및 데이터 추출
      const svgElement = document.querySelector('#directQrCodeContainer svg');
      const svgData = new XMLSerializer().serializeToString(svgElement);
      
      // SVG 데이터를 Base64로 인코딩
      const svgBase64 = btoa(unescape(encodeURIComponent(svgData)));
      
      // 이미지 생성 및 로드
      const img = new Image();
      img.onload = function() {
        // Canvas 생성 및 이미지 그리기
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        
        // Canvas에서 PNG 데이터 URL 생성
        const pngUrl = canvas.toDataURL('image/png');
        
        // 다운로드 링크 생성 및 클릭
        const downloadLink = document.createElement('a');
        downloadLink.href = pngUrl;
        downloadLink.download = `direct-qrcode-${Date.now()}.png`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      };
      img.src = 'data:image/svg+xml;base64,' + svgBase64;
    } catch (error) {
      console.error('QR 코드 저장 오류:', error);
      alert(language === 'KR' 
        ? 'QR 코드 저장 중 오류가 발생했습니다.' 
        : 'Error saving QR code.');
    }
  };
  
  // QR 코드 복사 처리
  const handleCopyQR = () => {
    try {
      // SVG 요소 선택 및 데이터 추출
      const svgElement = document.querySelector('#directQrCodeContainer svg');
      const svgData = new XMLSerializer().serializeToString(svgElement);
      
      // SVG 데이터를 Base64로 인코딩
      const svgBase64 = btoa(unescape(encodeURIComponent(svgData)));
      
      // 이미지 생성 및 로드
      const img = new Image();
      img.onload = function() {
        // Canvas 생성 및 이미지 그리기
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        
        // Canvas에서 Blob 생성
        canvas.toBlob(blob => {
          // 새로운 ClipboardItem 생성
          const clipboardItem = new ClipboardItem({ 'image/png': blob });
          
          // 클립보드에 복사
          navigator.clipboard.write([clipboardItem])
            .then(() => {
              alert(language === 'KR' 
                ? 'QR 코드가 클립보드에 복사되었습니다!' 
                : 'QR code copied to clipboard!');
            })
            .catch(err => {
              console.error('클립보드 복사 오류:', err);
              alert(language === 'KR' 
                ? '클립보드에 복사할 수 없습니다. 브라우저가 이 기능을 지원하지 않을 수 있습니다.' 
                : 'Cannot copy to clipboard. Your browser may not support this feature.');
            });
        });
      };
      img.src = 'data:image/svg+xml;base64,' + svgBase64;
    } catch (error) {
      console.error('QR 코드 복사 오류:', error);
      alert(language === 'KR' 
        ? 'QR 코드 복사 중 오류가 발생했습니다. 브라우저가 이 기능을 지원하지 않을 수 있습니다.' 
        : 'Error copying QR code. Your browser may not support this feature.');
    }
  };
  
  // 새 QR 코드 만들기 (초기화)
  const handleNewQR = () => {
    setStep(1);
    setSelectedTemplate(null);
    setInputValue('');
    setQrValue('');
    setQrGenerated(false);
    setUserImage(null);
  };
  
  const [vCardData, setVCardData] = useState({
    name: '',
    title: '',
    company: '',
    email: '',
    phone: '',
    address: ''
  });
  
  const handleVCardChange = (field, value) => {
    const updatedData = { ...vCardData, [field]: value };
    setVCardData(updatedData);
    setInputValue(JSON.stringify(updatedData));
  };
  
  // vCard 템플릿 전용 입력 폼 렌더링
  const renderVCardForm = () => {
    return (
      <div className="qr-client-container">
        <header className="qr-header">
        <h1>{language === 'KR' ? 'QR 코드 스캐너' : 'QR Code Scanner'}</h1>
        </header>
        <main className="qr-main">

        
        <div className="qr-vcard-form">
          <div className="qr-form-group">
            <label htmlFor="name">
              {language === 'KR' ? '이름' : 'Name'}:
            </label>
            <input
              type="text"
              id="name"
              value={vCardData.name}
              onChange={(e) => handleVCardChange('name', e.target.value)}
              placeholder={language === 'KR' ? '이름을 입력하세요' : 'Enter name'}
              required
            />
          </div>
          
          <div className="qr-form-group">
            <label htmlFor="title">
              {language === 'KR' ? '직함' : 'Title'}:
            </label>
            <input
              type="text"
              id="title"
              value={vCardData.title}
              onChange={(e) => handleVCardChange('title', e.target.value)}
              placeholder={language === 'KR' ? '직함을 입력하세요' : 'Enter title'}
            />
          </div>
          
          <div className="qr-form-group">
            <label htmlFor="company">
              {language === 'KR' ? '회사' : 'Company'}:
            </label>
            <input
              type="text"
              id="company"
              value={vCardData.company}
              onChange={(e) => handleVCardChange('company', e.target.value)}
              placeholder={language === 'KR' ? '회사 이름을 입력하세요' : 'Enter company name'}
            />
          </div>
          
          <div className="qr-form-group">
            <label htmlFor="email">
              {language === 'KR' ? '이메일' : 'Email'}:
            </label>
            <input
              type="email"
              id="email"
              value={vCardData.email}
              onChange={(e) => handleVCardChange('email', e.target.value)}
              placeholder={language === 'KR' ? '이메일 주소를 입력하세요' : 'Enter email address'}
            />
          </div>
          
          <div className="qr-form-group">
            <label htmlFor="phone">
              {language === 'KR' ? '전화번호' : 'Phone'}:
            </label>
            <input
              type="tel"
              id="phone"
              value={vCardData.phone}
              onChange={(e) => handleVCardChange('phone', e.target.value)}
              placeholder={language === 'KR' ? '전화번호를 입력하세요' : 'Enter phone number'}
            />
          </div>
          
          <div className="qr-form-group">
            <label htmlFor="address">
              {language === 'KR' ? '주소' : 'Address'}:
            </label>
            <textarea
              id="address"
              value={vCardData.address}
              onChange={(e) => handleVCardChange('address', e.target.value)}
              placeholder={language === 'KR' ? '주소를 입력하세요' : 'Enter address'}
              rows={3}
            />
          </div>
        </div>
        </main>
      </div>
    );
  };
  
  // 이미지 업로드 단계 UI 렌더링
  const renderImageUpload = () => {
    return (
      <div className="qr-image-upload">
        <h3>
          {language === 'KR' 
            ? '이미지에 QR 코드 삽입하기' 
            : 'Embed QR Code on Image'}
        </h3>
        
        {!userImage ? (
          <div className="qr-upload-container">
            <p>
              {language === 'KR' 
                ? 'QR 코드를 삽입할 이미지를 업로드하세요' 
                : 'Upload an image to embed the QR code'}
            </p>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="qr-file-input"
            />
            <button
              className="qr-btn"
              onClick={() => document.querySelector('.qr-file-input').click()}
            >
              {language === 'KR' ? '이미지 선택' : 'Select Image'}
            </button>
          </div>
        ) : (
          <div className="qr-image-editor">
            <div 
              className="qr-image-container"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <img 
                src={userImage} 
                alt="User uploaded" 
                className="qr-user-image" 
                ref={imageRef}
              />
              <div 
                className="qr-overlay-container" 
                id="overlayQrCodeContainer" 
                style={{
                  position: 'absolute',
                  left: `${qrPosition.x}%`,
                  top: `${qrPosition.y}%`,
                  transform: 'translate(-50%, -50%)',
                  width: `${qrSize}%`,
                  height: 'auto',
                  cursor: isDragging ? 'grabbing' : 'grab',
                  opacity: qrOpacity
                }}
              >
                <QRCodeSVG
                  value={qrValue}
                  size="100%"
                  level="H"
                  includeMargin={true}
                  style={{
                    width: '100%',
                    height: '100%',
                    filter: 'grayscale(100%)'
                  }}
                />
              </div>
            </div>
            
            <div className="qr-image-controls">
              <div className="qr-control-group">
                <label htmlFor="qr-size">
                  {language === 'KR' ? 'QR 코드 크기' : 'QR Code Size'}:
                </label>
                <input
                  type="range"
                  id="qr-size"
                  min="5"
                  max="50"
                  value={qrSize}
                  onChange={(e) => setQrSize(parseInt(e.target.value))}
                />
                <span>{qrSize}%</span>
              </div>
              
              <div className="qr-control-group">
                <label htmlFor="qr-opacity">
                  {language === 'KR' ? '불투명도' : 'Opacity'}:
                </label>
                <input
                  type="range"
                  id="qr-opacity"
                  min="0.1"
                  max="1"
                  step="0.1"
                  value={qrOpacity}
                  onChange={(e) => setQrOpacity(parseFloat(e.target.value))}
                />
                <span>{Math.round(qrOpacity * 100)}%</span>
              </div>
              
              <p className="qr-drag-instruction">
                {language === 'KR'
                  ? 'QR 코드를 드래그하여 원하는 위치로 이동하세요'
                  : 'Drag the QR code to position it'}
              </p>
              
              <div className="qr-image-action-buttons">
                <button
                  className="qr-btn"
                  onClick={saveQrWithImage}
                >
                  {language === 'KR' ? '이미지 저장' : 'Save Image'}
                </button>
                
                <button
                  className="qr-btn qr-btn-secondary"
                  onClick={() => {
                    setUserImage(null);
                    setStep(3);
                  }}
                >
                  {language === 'KR' ? '다른 이미지 사용' : 'Use Another Image'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className="qr-creator-client-container">
      <header className="qr-creator-scanner-header">
      <h1>{language === 'KR' ? 'QR 코드 생성기' : 'QR Code Creator'}</h1>
      </header>
      <main className="qr-creator-scanner-main">
        <div className="direct-qr-creator">
          <h2>{language === 'KR' ? 'Direct QR 코드 만들기' : 'Create Direct QR Code'}</h2>
          
          {/* 단계 표시기 */}
          <div className="qr-steps">
            <div className={`qr-step ${step >= 1 ? 'active' : ''}`}>
              {language === 'KR' ? '템플릿 선택' : 'Select Template'}
            </div>
            <div className={`qr-step ${step >= 2 ? 'active' : ''}`}>
              {language === 'KR' ? '내용 입력' : 'Enter Content'}
            </div>
            <div className={`qr-step ${step >= 3 ? 'active' : ''}`}>
              {language === 'KR' ? 'QR 코드 생성' : 'Generate QR Code'}
            </div>
            <div className={`qr-step ${step >= 4 ? 'active' : ''}`}>
              {language === 'KR' ? '이미지에 삽입' : 'Embed on Image'}
            </div>
          </div>
          
          {/* 단계 1: 템플릿 선택 */}
          {step === 1 && (
            <div className="qr-template-selection">
              <h3>{language === 'KR' ? 'QR 코드 유형을 선택하세요' : 'Select QR Code Type'}</h3>
              <div className="qr-template-grid">
                {templates.map(template => (
                  <div 
                    key={template.id}
                    className="qr-template-card"
                    onClick={() => handleTemplateSelect(template)}
                  >
                    <div className="qr-template-preview">
                      <i className={template.icon}></i>
                    </div>
                    <h3>{template.name}</h3>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* 단계 2: 내용 입력 */}
          {step === 2 && selectedTemplate && (
            <div className="qr-content-input">
              <h3>
                {language === 'KR' ? `${selectedTemplate.name} 내용을 입력하세요` : `Enter ${selectedTemplate.name} Content`}
              </h3>
              
              {selectedTemplate.id === 'vcard' ? (
                renderVCardForm()
              ) : (
                <div className="qr-form-group">
                  <label htmlFor="qr-content">
                    {selectedTemplate.id === 'url' ? (
                      language === 'KR' ? 'URL을 입력하세요 (http:// 또는 https://로 시작)' : 'Enter URL (starting with http:// or https://)'
                    ) : selectedTemplate.id === 'email' ? (
                      language === 'KR' ? '이메일 주소를 입력하세요' : 'Enter email address'
                    ) : selectedTemplate.id === 'phone' ? (
                      language === 'KR' ? '전화번호를 입력하세요' : 'Enter phone number'
                    ) : selectedTemplate.id === 'sms' ? (
                      language === 'KR' ? 'SMS 메시지를 입력하세요' : 'Enter SMS message'
                    ) : (
                      language === 'KR' ? '내용을 입력하세요' : 'Enter content'
                    )}:
                  </label>
                  
                  {selectedTemplate.id === 'text' || selectedTemplate.id === 'sms' ? (
                    <textarea
                      id="qr-content"
                      value={inputValue}
                      onChange={handleInputChange}
                      rows={5}
                      placeholder={
                        selectedTemplate.id === 'text' 
                          ? (language === 'KR' ? '텍스트를 입력하세요...' : 'Enter text...') 
                          : (language === 'KR' ? 'SMS 메시지를 입력하세요...' : 'Enter SMS message...')
                      }
                    />
                  ) : (
                    <input
                      type={selectedTemplate.id === 'email' ? 'email' : selectedTemplate.id === 'phone' ? 'tel' : 'text'}
                      id="qr-content"
                      value={inputValue}
                      onChange={handleInputChange}
                      placeholder={
                        selectedTemplate.id === 'url' 
                          ? 'example.com' 
                          : selectedTemplate.id === 'email' 
                            ? 'email@example.com' 
                            : selectedTemplate.id === 'phone' 
                              ? '+1234567890' 
                              : ''
                      }
                    />
                  )}
                </div>
              )}
              
              <div className="qr-form-actions">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="qr-btn qr-btn-secondary"
                >
                  {language === 'KR' ? '이전' : 'Back'}
                </button>
                <button
                  type="button"
                  onClick={handleGenerateQR}
                  className="qr-btn"
                >
                  {language === 'KR' ? 'QR 코드 생성' : 'Generate QR Code'}
                </button>
              </div>
            </div>
          )}
          
          {/* 단계 3: QR 코드 생성 결과 */}
          {step === 3 && qrGenerated && (
            <div className="qr-result">
              <h3>
                {language === 'KR' 
                  ? `${qrTitle} QR 코드가 생성되었습니다` 
                  : `${qrTitle} QR Code Generated`}
              </h3>
              
              <div id="directQrCodeContainer" className="qr-code-container">
                <QRCodeSVG
                  value={qrValue}
                  size={250}
                  level="H"
                  includeMargin={true}
                />
              </div>
              
              <div className="qr-actions">
                <button
                  onClick={handleSaveQR}
                  className="qr-btn"
                >
                  {language === 'KR' ? 'QR 코드 저장' : 'Save QR Code'}
                </button>
                <button
                  onClick={handleCopyQR}
                  className="qr-btn"
                >
                  {language === 'KR' ? 'QR 코드 복사' : 'Copy QR Code'}
                </button>
                <button
                  onClick={() => {
                    const fileInput = document.createElement('input');
                    fileInput.type = 'file';
                    fileInput.accept = 'image/*';
                    fileInput.onchange = handleImageUpload;
                    fileInput.click();
                  }}
                  className="qr-btn"
                >
                  {language === 'KR' ? '이미지에 QR 코드 삽입' : 'Embed on Image'}
                </button>
                <button
                  onClick={handleNewQR}
                  className="qr-btn qr-btn-secondary"
                >
                  {language === 'KR' ? '새 QR 코드 만들기' : 'Create New QR Code'}
                </button>
              </div>
            </div>
          )}
          
          {/* 단계 4: 이미지에 QR 코드 삽입 */}
          {step === 4 && qrGenerated && renderImageUpload()}
        </div>
      </main>
    </div>
  );
};

export default DirectQRCreator;